import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import {
  Home,
  CheckCircle,
  LocalShipping,
  Inventory,
  Build,
  CleaningServices,
  AttachMoney
} from '@mui/icons-material';

function PrivateMove() {
  const packages = [
    {
      name: 'Basis-Paket',
      price: 'ab 299€',
      features: [
        'Transport Ihrer Möbel',
        'Bereitstellung des Umzugswagens',
        'Grundversicherung inklusive',
        'Trageservice bis 2. Etage'
      ]
    },
    {
      name: 'Komfort-Paket',
      price: 'ab 599€',
      highlight: true,
      features: [
        'Alle Leistungen des Basis-Pakets',
        'Verpackungsmaterial inklusive',
        'Demontage & Montage der Möbel',
        'Einpackservice für zerbrechliche Gegenstände'
      ]
    },
    {
      name: 'Premium-Paket',
      price: 'ab 999€',
      features: [
        'Alle Leistungen des Komfort-Pakets',
        'Kompletter Ein- und Auspackservice',
        'Endreinigung der alten Wohnung',
        'Einrichtung der neuen Wohnung'
      ]
    }
  ];

  const steps = [
    { number: '1', title: 'Anfrage', desc: 'Kontaktieren Sie uns für ein unverbindliches Angebot' },
    { number: '2', title: 'Besichtigung', desc: 'Wir schauen uns Ihre Wohnung an und planen den Umzug' },
    { number: '3', title: 'Angebot', desc: 'Sie erhalten ein transparentes Festpreisangebot' },
    { number: '4', title: 'Umzugstag', desc: 'Unser Team führt Ihren Umzug professionell durch' }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Home sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Privatumzug
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Stressfrei in Ihr neues Zuhause
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Ob kleine Wohnung oder großes Haus – wir kümmern uns um Ihren Umzug. 
            Professionell, zuverlässig und zu fairen Preisen.
          </Typography>
        </Box>

        {/* Process Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            So einfach geht's
          </Typography>
          <Grid container spacing={3}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: '#1a3a44',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.08)',
                    height: '100%',
                    textAlign: 'center'
                  }}
                >
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: '#55d6dc',
                    color: '#0a1f28',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}>
                    {step.number}
                  </Box>
                  <Typography variant="h6" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.85 }}>
                    {step.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Packages */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Unsere Umzugspakete
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {packages.map((pkg, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card sx={{
                  bgcolor: '#1a3a44',
                  borderRadius: 2,
                  border: pkg.highlight ? '2px solid #ffd700' : '1px solid rgba(255,255,255,0.08)',
                  height: '100%',
                  position: 'relative',
                  overflow: 'visible'
                }}>
                  {pkg.highlight && (
                    <Chip
                      label="BELIEBT"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: 20,
                        bgcolor: '#ffd700',
                        color: '#0a1f28',
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h5" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                      {pkg.name}
                    </Typography>
                    <Typography variant="h3" sx={{ color: '#55d6dc', mb: 3, fontWeight: 'bold' }}>
                      {pkg.price}
                    </Typography>
                    <List>
                      {pkg.features.map((feature, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckCircle sx={{ color: '#55d6dc', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                            sx={{ color: 'white', opacity: 0.9 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Additional Services */}
        <Card sx={{ 
          bgcolor: 'rgba(255,255,255,0.05)', 
          backdropFilter: 'blur(10px)',
          mb: 6
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
              Zusätzliche Services
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Inventory sx={{ color: '#ffd700', fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      Verpackungsservice
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                      Professionelles Verpacken Ihrer Gegenstände
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Build sx={{ color: '#ffd700', fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      Möbelmontage
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                      Demontage und Montage Ihrer Möbel
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CleaningServices sx={{ color: '#ffd700', fontSize: 40 }} />
                  <Box>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      Endreinigung
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                      Übergabefertige Reinigung der alten Wohnung
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Box sx={{ 
          bgcolor: '#275664', 
          borderRadius: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <AttachMoney sx={{ fontSize: 60, color: '#ffd700', mb: 2 }} />
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Jetzt kostenloses Angebot einholen
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 3 }}>
            Nutzen Sie unseren Kalkulator oder kontaktieren Sie uns direkt
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Box
              component="button"
              onClick={() => window.location.href = '/calculator'}
              style={{
                backgroundColor: '#55d6dc',
                color: '#275664',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
            >
              Zum Kalkulator
            </Box>
            <Box
              component="button"
              onClick={() => window.location.href = '/contact'}
              style={{
                backgroundColor: 'transparent',
                color: '#ffd700',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: '2px solid #ffd700',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,215,0,0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Kontakt aufnehmen
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default PrivateMove;